import { MdOutlineLogout as LogoutIcon } from 'react-icons/md';
import { ICountry, IVehicleUsages } from './interfaces';
import { ACCESS_RIGHTS, PRODUCTS, COMPANY_TYPES, VEHICLE_BODY_TYPES } from './enums';

export const PERSISTENT_STORAGE_KEY = 'adm-k89';

import { FaHome } from 'react-icons/fa';
import { FaCar } from 'react-icons/fa';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { Award, Banknote, BanknoteArrowUp, BookOpen, Building, Building2, Calculator, Camera, Clipboard, Cog, CreditCard, Database, DatabaseBackup, DollarSign, FilesIcon, FileText, FileX, Fuel, Group, HandCoins, LayoutDashboard, RefreshCcw, RotateCw, Settings, Shield, ShoppingCart, Signature, Target, ThumbsUp, Users, Wallet, WalletCardsIcon } from 'lucide-react';
import { ArrowPathIcon } from '@heroicons/react/20/solid';

export const SUPPORTED_COUNTRIES = ["GH", "NG"];

export const COUNTRIES: ICountry[] = []

export type SupportedCountry = typeof SUPPORTED_COUNTRIES[number];

export type Currency = 'NGN' | 'GHS' | 'USD' | 'ZAR' | 'KES' | 'XOF';

export const COUNTRY_NAMES: Record<SupportedCountry, string> = {
  GH: "Ghana",
  NG: "Nigeria",
};

export const SIDE_BAR_NAVS = {
  main: [
    {
      title: 'Overview',
      path: '/dashboard',
      icon: LayoutDashboard,
      access: [
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.AGENT,
        ACCESS_RIGHTS.OWNER,
      ],
      key: 'dashboard',
    }
  ],

  manage: [
    {
      title: 'Quotes',
      path: '/quotes',
      icon: Calculator,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.AGENT,
      ],
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.AGENCY],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE, PRODUCTS.PREMIUM_FINANCING],
      key: 'quotes'
    },
    {
      title: 'Policies',
      path: '/policies',
      icon: FileText,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.AGENT,
      ],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE, PRODUCTS.PREMIUM_FINANCING, PRODUCTS.AI_VEHICLE_VERIFICATION],
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.BROKER, COMPANY_TYPES.INSURER, COMPANY_TYPES.FLEET, COMPANY_TYPES.AGENCY],
      key: 'policies',
      hasChildren: true,
      children: [
        {
          title: 'Motor',
          id: 0,
          path: '/policies?type=motor',
          icon: FaCar
        },
        // Non-Motor hidden from the system (UI entry point only; route still resolves)
        // {
        //   title: 'Non Motor',
        //   id: 1,
        //   path: '/policies?type=non-motor',
        //   icon: Shield
        // }
      ]
    },
    {
      title: 'Renewals',
      path: '/renewals',
      icon: RotateCw,
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.ADMINISTRATOR, ACCESS_RIGHTS.AGENT, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.SUPERVISOR],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.BROKER, COMPANY_TYPES.INSURER],
      key: 'renewals'
    },
    {
      title: 'Inspections',
      path: '/vehicle-verification',
      icon: Camera,
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.ADMINISTRATOR, ACCESS_RIGHTS.AGENT, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.SUPERVISOR],
      products: [PRODUCTS.AI_VEHICLE_VERIFICATION],
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.INSURER],
      key: 'vehicle-verification'
    },
    // Loans — two SIDE_BAR_NAVS entries covering two distinct audiences with
    // different access rules. The renderer treats them as separate entries
    // (same title/path/icon), so the sidebar de-dupes by path and only one
    // "Loans" link appears per user; the split is purely about the access
    // filter matrix.
    //
    // 1) Financier audience — ALL roles. Loans + Repayments are the
    //    financier's core workflow; when the sidebar entry was tightened to
    //    Internal-only in v1.6.3 they lost access entirely and had to be
    //    restored on request.
    // 2) Brolly Internal audience — OWNER + ADMINISTRATOR only, matching
    //    the Premium Financing gate.
    //
    // Route in src/routes/index.tsx uses a mixed guard (any financier OR
    // Internal OWNER/ADMIN) so URL-typing matches whichever entry the user
    // sees in the sidebar.
    {
      title: 'Loans',
      path: '/loans',
      icon: Wallet,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.AGENT,
      ],
      products: [PRODUCTS.PREMIUM_FINANCING],
      teamTypes: [COMPANY_TYPES.FINANCIER],
      key: 'loans-financier'
    },
    {
      title: 'Loans',
      path: '/loans',
      icon: Wallet,
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.ADMINISTRATOR],
      products: [PRODUCTS.PREMIUM_FINANCING],
      teamTypes: [COMPANY_TYPES.INTERNAL],
      key: 'loans-internal'
    },
    {
      title: 'RNPL',
      path: '/rnpl',
      icon: Cog,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.SUPERVISOR,
      ],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      teamTypes: [COMPANY_TYPES.INTERNAL],
      key: 'rnpl'
    },
    // Premium Financing — re-enabled for internal OWNER + ADMINISTRATOR
    // roles on INTERNAL company only.
    //   * access = [OWNER, ADMINISTRATOR] — most Brolly-internal folks
    //     work as OWNER, not ADMIN, so restricting to ADMIN-only hides
    //     it from the workspace owner. Including both covers both.
    //   * teamTypes = [INTERNAL] — keeps insurers/brokers/agencies/
    //     financiers out.
    // Route-level guard in InternalAdminRouteWrapper.tsx enforces the
    // same combination for URL-typing paths. Loans (the companion
    // feature) stays hidden — client only wants Premium Financing
    // surfaced.
    {
      title: 'Premium Financing',
      path: '/premium-financing',
      icon: Banknote,
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.ADMINISTRATOR],
      products: [PRODUCTS.PREMIUM_FINANCING],
      teamTypes: [COMPANY_TYPES.INTERNAL],
      key: 'financing'
    },
    {
      title: 'Claims',
      path: '/claims',
      icon: FileX,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.AGENT,
      ],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.PREMIUM_FINANCING],
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.AGENCY, COMPANY_TYPES.BROKER],
      hasChildren: true,
      children: [
        {
          title: 'Motor',
          id: 2,
          path: '/claims?type=motor',
          icon: FaCar
        },
        // Non-Motor hidden from the system (UI entry point only; route still resolves)
        // {
        //   title: 'Non Motor',
        //   id: 3,
        //   path: '/claims?type=non-motor',
        //   icon: Shield
        // },
      ],
      key: 'claims'
    },
    {
      title: 'Customers',
      path: '/customers',
      icon: Users,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.ADMINISTRATOR,
      ],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE, PRODUCTS.PREMIUM_FINANCING],
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.BROKER],
      key: 'customers'
    },
    {
      title: 'Sales',
      path: '/sales',
      icon: ShoppingCart,
      access: [
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.AGENT,
      ],
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.AGENCY, COMPANY_TYPES.FUEL_PROVIDER,],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      key: 'sales'
    },
    {
      title: 'Transactions',
      path: '/transactions',
      icon: RefreshCcw,
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.ADMINISTRATOR, ACCESS_RIGHTS.SUPERVISOR, ACCESS_RIGHTS.MANAGER],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE, PRODUCTS.PREMIUM_FINANCING, PRODUCTS.AI_VEHICLE_VERIFICATION],
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.FINANCIER],
      key: 'transactions'
    },
    {
      title: 'Leads',
      path: '/leads',
      icon: Target,
      access: [
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.AGENT,
        ACCESS_RIGHTS.OWNER,
      ],
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.RIDE_SHARING],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE, PRODUCTS.PREMIUM_FINANCING],
      key: 'leads'
    },
    {
      title: 'Companies',
      path: '/teams',
      icon: Building,
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.ADMINISTRATOR, ACCESS_RIGHTS.SUPERVISOR],
      teamTypes: [COMPANY_TYPES.INTERNAL],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      key: 'teams'
    },
    {
      title: 'Activations',
      path: '/broker-activations',
      icon: ThumbsUp,
      access: [ACCESS_RIGHTS.OWNER],
      teamTypes: [COMPANY_TYPES.INTERNAL],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      key: 'broker-activations'
    },
    {
      title: 'Bulk Uploads',
      path: '/mapping-tool/broker-uploads',
      icon: Database,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.MANAGER,
      ],
      teamTypes: [COMPANY_TYPES.INTERNAL],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      key: 'mapping-tool-broker-uploads'
    },
    // TEMPORARY FEATURE — POLICY EXPORT RESTORE (Brolly Internal only).
    // One-off tool for loading the policies_<broker>.csv database exports back
    // in. Delete this entry when the restore is done — see
    // views/admin/policyRestore/PolicyRestorePage.tsx for the full file list.
    {
      title: 'Direct Bulk Upload',
      path: '/policy-restore',
      icon: DatabaseBackup,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.SUPERVISOR,
      ],
      teamTypes: [COMPANY_TYPES.INTERNAL],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      key: 'policy-restore'
    },
    {
      title: 'Insurers',
      path: '/insurers',
      icon: Building2,
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.SUPERVISOR],
      teamTypes: [COMPANY_TYPES.INTERNAL],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      key: 'insurers'
    },
    {
      title: 'Blog',
      path: '/blogs',
      icon: BookOpen,
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.ADMINISTRATOR, ACCESS_RIGHTS.SUPERVISOR],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      teamTypes: [COMPANY_TYPES.INTERNAL],
      key: 'blog'
    },
    {
      title: 'Resources',
      path: '/resources',
      icon: FilesIcon,
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.ADMINISTRATOR,],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      // Hidden from INSURER and RIDE_SHARING sidebars per the insurer-side-menu
      // spec ("Remove extra sections — reviews & resources"). NOTE: an empty
      // teamTypes array reads as "no filter, show to everyone" in the sidebar
      // filter (see NavigationSection.tsx:47), which is what previously leaked
      // this item back into the insurer sidebar. Enumerate allowed types
      // explicitly so the filter actually hides it.
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.BROKER],
      key: 'resources'
    },
    {
      title: 'Quota',
      path: '/quota',
      icon: ChartBarIcon,
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.SUPERVISOR, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.ADMINISTRATOR,],
      products: [PRODUCTS.AUTO_INSURANCE,],
      teamTypes: [COMPANY_TYPES.FUEL_PROVIDER],
      key: 'reviews'
    },
    {
      title: 'Fuel Audit',
      path: '/fuel-audit',
      icon: Fuel,
      access: [ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.ADMINISTRATOR],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.FUEL_PROVIDER],
      key: 'audit'
    },
    {
      title: 'Reviews',
      path: '/reviews',
      icon: ThumbsUp,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.MANAGER,
      ],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      // Hidden from INSURER and RIDE_SHARING sidebars — same reason as Resources
      // above. Empty teamTypes[] was leaking this into every sidebar; enumerate
      // allowed teamTypes explicitly so the filter actually excludes insurer.
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.BROKER],
      key: 'reviews'
    }
  ],

  financials: [
    {
      title: 'Commissions',
      path: '/commissions',
      icon: Award,
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.ADMINISTRATOR, ACCESS_RIGHTS.SUPERVISOR, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.AGENT],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.BROKER],
      key: 'commissions'
    },
    // Premium Payouts is hidden from the side menu in this release
    // (both the internal/insurer/ride-sharing variant and the broker
    // variant). Payout routes/APIs stay live; restore when payouts is
    // being surfaced again.
    // {
    //   title: 'Premium Payouts',
    //   path: '/payouts',
    //   icon: Wallet,
    //   access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.SUPERVISOR, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.AGENT],
    //   products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
    //   teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.INSURER, COMPANY_TYPES.RIDE_SHARING,],
    //   key: 'payouts'
    // },
    // {
    //   title: 'Premium Payouts',
    //   path: '/payouts',
    //   icon: Wallet,
    //   access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.SUPERVISOR, ACCESS_RIGHTS.MANAGER,],
    //   products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
    //   teamTypes: [COMPANY_TYPES.BROKER],
    //   key: 'payouts'
    // },
    {
      title: 'Settlements',
      path: '/settlements',
      icon: HandCoins,
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.SUPERVISOR, ACCESS_RIGHTS.MANAGER, ACCESS_RIGHTS.AGENT],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.RIDE_SHARING],
      key: 'settlements'
    },
    {
      title: 'Disbursements',
      path: '/disbursements',
      icon: WalletCardsIcon,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.SUPERVISOR,
      ],
      products: [PRODUCTS.PREMIUM_FINANCING],
      teamTypes: [COMPANY_TYPES.FINANCIER, COMPANY_TYPES.INTERNAL],
      key: 'disbursements'
    },
    {
      title: 'Payments',
      path: '/payments',
      icon: CreditCard,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.ADMINISTRATOR,
      ],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.AGENCY, COMPANY_TYPES.BROKER],
      key: 'payments'
    },
    {
      title: 'Bills',
      path: '/bills',
      icon: BanknoteArrowUp,
      access: [
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.OWNER,
      ],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE],
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.BROKER, COMPANY_TYPES.INSURER],
      key: 'bills'
    },
  ],

  system: [

    {
      title: 'Audit Logs',
      path: '/audit',
      icon: Clipboard,
      access: [ACCESS_RIGHTS.OWNER, ACCESS_RIGHTS.SUPERVISOR, ACCESS_RIGHTS.MANAGER],
      teamTypes: [COMPANY_TYPES.INTERNAL],
      key: 'audit'
    },
    {
      title: 'Audit Logs',
      path: '/audit',
      icon: Clipboard,
      access: [ACCESS_RIGHTS.OWNER],
      teamTypes: [COMPANY_TYPES.BROKER, COMPANY_TYPES.FINANCIER, COMPANY_TYPES.AGENCY, COMPANY_TYPES.FUEL_PROVIDER, COMPANY_TYPES.FLEET],
      key: 'audit'
    },
    {
      title: 'Settings',
      path: '/settings',
      icon: Settings,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.AGENT,
      ],
      key: 'settings'
    }
  ],

  bottom: [
    {
      title: 'Log out',
      path: '/settings',
      icon: LogoutIcon,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.AGENT,
      ],
      key: 'settings'
    }
  ]
};

export const BROKER_SIDE_BAR_NAVS = {
  main: [
    {
      title: 'Dashboard Overview',
      path: '/dashboard',
      icon: LayoutDashboard,
      access: [
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.AGENT,
        ACCESS_RIGHTS.OWNER,
      ],
      key: 'dashboard',
    },
    {
      title: 'Policies Management',
      path: '/policies',
      icon: FileText,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.AGENT,
      ],
      products: [PRODUCTS.AUTO_INSURANCE, PRODUCTS.HOME_INSURANCE, PRODUCTS.PREMIUM_FINANCING, PRODUCTS.AI_VEHICLE_VERIFICATION],
      teamTypes: [COMPANY_TYPES.INTERNAL, COMPANY_TYPES.BROKER, COMPANY_TYPES.INSURER, COMPANY_TYPES.FLEET, COMPANY_TYPES.AGENCY],
      key: 'policies',
      hasChildren: true,
      children: [
        {
          title: 'Motor',
          id: 0,
          path: '/policies?type=motor',
          icon: FaCar
        },
        // Non-Motor hidden from the system (UI entry point only; route still resolves)
        // {
        //   title: 'Non motor',
        //   id: 1,
        //   path: '/policies?type=non-motor',
        //   icon: Shield
        // },
        {
          title: 'Renewals',
          id: 2,
          path: '/renewals',
          icon: RotateCw
        }
      ]
    },
    {
      title: 'Claims and More tools',
      path: '/claims-locked',
      icon: FileX,
      locked: true,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.AGENT,
      ],
      key: 'claims-locked'
    },
    {
      title: 'Settings',
      path: '/settings',
      icon: Settings,
      access: [
        ACCESS_RIGHTS.OWNER,
        ACCESS_RIGHTS.SUPERVISOR,
        ACCESS_RIGHTS.MANAGER,
        ACCESS_RIGHTS.ADMINISTRATOR,
        ACCESS_RIGHTS.AGENT,
      ],
      key: 'settings'
    },
  ],
};

export const VEHICLE_USAGES_FOR_ISSUED_POLICIES: Record<string, IVehicleUsages> = {
  ARTICULATOR_HEAD: {
    title: 'ARTICULATOR_HEAD',
    description: ''
  },
  BUS: {
    title: 'BUS',
    description: ''
  },
  GENERAL_CARTAGE_ABOVE_3000: {
    title: 'GENERAL_CARTAGE_ABOVE_3000',
    description: ''
  },
  GENERAL_CARTAGE_BELOW_3000: {
    title: 'GENERAL_CARTAGE_BELOW_3000',
    description: ''
  },
  HAILING_SERVICE: {
    title: 'HAILING_SERVICE',
    description: 'Comprises of Uber, Bolt, Yango, ...etc'
  },
  HIRING_CAR: {
    title: 'HIRING_CAR',
    description: ''
  },
  MOTORCYCLE: {
    title: 'MOTORCYCLE',
    description:
      'A motorcycle is a two or three-wheeled motor vehicle steered by a handlebar from a saddle-style seat. Motorcycle design varies greatly to suit a range of different purposes: long-distance travel, commuting, cruising, sport, and off-road riding'
  },
  OWN_GOODS_CARRYING_VEHICLE_BELOW_3000: {
    title: 'OWN_GOODS_CARRYING_VEHICLE_BELOW_3000',
    description: ''
  },
  OWN_GOODS_CARRYING_VEHICLE_OVER_3000: {
    title: 'OWN_GOODS_CARRYING_VEHICLE_OVER_3000',
    description: ''
  },
  PRIVATE_USE_COMPANY_OWNED: {
    title: 'PRIVATE_USE_COMPANY_OWNED',
    description: ''
  },
  PRIVATE_USE_OWN_VEHICLE: {
    title: 'PRIVATE_USE_OWN_VEHICLE',
    description: ''
  },
  SPECIAL_TYPE_SITE: {
    title: 'SPECIAL_TYPE_SITE',
    description: ''
  },
  SPECIAL_TYPE_SITE_ROAD: {
    title: 'SPECIAL_TYPE_SITE_ROAD',
    description: ''
  },
  TAXI: {
    title: 'TAXI',
    description: ''
  },
  TRADE_PLATE: {
    title: 'TRADE_PLATE',
    description: ''
  }
};

export const VEHICLE_USAGES: Record<string, IVehicleUsages> = {
  BUS: {
    title: 'Bus (Private or Commercial use)',
    description: ''
  },
  PRIVATE_USE_OWN_VEHICLE: {
    title: 'Private (Individual Owned)',
    description: ''
  },
  SPECIAL_TYPE_SITE: {
    title: 'Special Type (Site use only)',
    description: ''
  },
  PRIVATE_USE_COMPANY_OWNED: {
    title: 'Private (Company Owned)',
    description: ''
  },
  GENERAL_CARTAGE_ABOVE_3000: {
    title: 'General Cartage (Above 3000 cc)',
    description: ''
  },
  HIRING_CAR: {
    title: 'Hiring Car',
    description: ''
  },
  OWN_GOODS_CARRYING_VEHICLE_BELOW_3000: {
    title: 'Own Goods Carrying Vehicle (Below 3000 cc)',
    description: ''
  },
  HAILING_SERVICE: {
    title: 'Hailing Services (Uber/Bolt/Yango ...)',
    description: 'Comprises of Uber, Bolt, Yango, ...etc'
  },
  TRADE_PLATE: {
    title: 'Trade Plate (Personal Use only)',
    description: ''
  },
  GENERAL_CARTAGE_BELOW_3000: {
    title: 'General Cartage (Below 3000 cc)',
    description: ''
  },
  TAXI: {
    title: 'Taxi',
    description: ''
  },
  OWN_GOODS_CARRYING_VEHICLE_OVER_3000: {
    title: 'Own Goods Carrying Vehicle (Above 3000 cc)',
    description: ''
  },
  SPECIAL_SITE_TYPE_ROAD: {
    title: 'Special Type (Site use and Road use)',
    description: ''
  },
  ARTICULATOR_HEAD: {
    title: 'Articulated Truck Head',
    description: ''
  },
  MOTORCYCLE: {
    title: 'Motorcycle',
    description:
      'A motorcycle is a two or three-wheeled motor vehicle steered by a handlebar from a saddle-style seat. Motorcycle design varies greatly to suit a range of different purposes: long-distance travel, commuting, cruising, sport, and off-road riding'
  }
};

export const VEHICLE_COVER_TYPES: Record<string, IVehicleUsages> = {
  THIRD_PARTY_FIRE_THEFT: {
    title: 'Third Party (Fire/Theft)',
    description: ''
  },
  COMPREHENSIVE_85: {
    title: '85% Comprehensive',
    description: ''
  },
  COMPREHENSIVE_90: {
    title: '90% Comprehensive',
    description: ''
  },
  THIRD_PARTY: {
    title: 'Third Party',
    description: ''
  },
  COMPREHENSIVE_100: {
    title: '100% Comprehensive',
    description: ''
  },
  SMART_DRIVE: {
    title: 'Smart Drive',
    description: ''
  }
};

export const VEHICLE_COLORS = [
  'White',
  'Black',
  'Gray',
  'Silver',
  'Red',
  'Blue',
  'Brown',
  'Green',
  'Beige',
  'Orange',
  'Yellow'
];

export const GHANA_REGIONS = [
  'Ahafo',
  'Ashanti',
  'Bono East',
  'Brong Ahafo',
  'Central',
  'Eastern',
  'Greater-Accra',
  'Western',
  'Western North',
  'Northern',
  'North-East',
  'Oti',
  'Savannah',
  'Upper East',
  'Upper West',
  'Volta'
];

export const REGIONS_BY_COUNTRY: Record<any, string[]> = {
  GH: [
    "Ahafo",
    "Ashanti",
    "Bono East",
    "Brong Ahafo",
    "Central",
    "Eastern",
    "Greater-Accra",
    "Western",
    "Western North",
    "Northern",
    "North-East",
    "Oti",
    "Savannah",
    "Upper East",
    "Upper West",
    "Volta",
  ],
  NG: [
    "Abia",
    "Adamawa",
    "Akwa Ibom",
    "Anambra",
    "Bauchi",
    "Bayelsa",
    "Benue",
    "Borno",
    "Cross River",
    "Delta",
    "Ebonyi",
    "Edo",
    "Ekiti",
    "Enugu",
    "FCT",
    "Gombe",
    "Imo",
    "Jigawa",
    "Kaduna",
    "Kano",
    "Katsina",
    "Kebbi",
    "Kogi",
    "Kwara",
    "Lagos",
    "Nasarawa",
    "Niger",
    "Ogun",
    "Ondo",
    "Osun",
    "Oyo",
    "Plateau",
    "Rivers",
    "Sokoto",
    "Taraba",
    "Yobe",
    "Zamfara",
  ],
};

export const SERVER_DATE_FORMAT = 'MM-DD-YYYY';

export const INPUT_DATE_FORMAT = 'YYYY-MM-DD';

export const ID_TYPES = ['National ID', 'Passport', "Voter's ID", "Driver's Lincense"];

export const REGISTRATION_NUMBER_PATTERNS: Record<string, RegExp> = {
  GH: /^[A-Z]{2}\s?\d{1,4}(-\d{2})?$/, // GH pattern: e.g., GR 1234-22 or GR 1234
  NG: /^[A-Z]{3}\s?\d{3}[A-Z]{2}$/, // NG pattern: e.g., ABC 123DE
}

export const REGISTRATION_NUMBER_ERROR_MESSAGE: Record<string, string> = {
  GH: 'Invalid format. Please use 2 capital letters, space, then up to 5 numbers or letters. Do not add the suffix',
  NG: 'Invalid format. Please use 3 capital letters, space, then 3 numbers, and two capital letters. Do not add the suffix'
}

export const PHONE_NUMBER_ERROR_MESSAGE: Record<string, string> = {
  GH: 'Enter a valid number',
  NG: 'Enter a valid number'
}

export const PHONE_NUMBER_PATTERNS: Record<string, RegExp> = {
  GH: /^(?:\+233[235][0-9]+|0[235][0-9]+)$/,
  NG: /^(?:\+234|234|0)(70|80|81|90|91)\d+$/
  ,
}

export const ID_NUMBER_PATTERNS: Record<string, RegExp> = {
  GH: /^GHA-\d{9}-\d$/, // GH pattern: e.g., GHA-000379275-9
  NG: /^(?:[A-Z0-9]{9,15}|\d{11})$/, // NG pattern: e.g., 11111100101
}

export const ID_NUMBER_ERROR_MESSAGE: Record<string, string> = {
  GH: 'Invalid format. Please start with GHA, then hiphen, then nine digits, then hiphen, and one last digit',
  NG: 'Invalid format. Please Enter a valid ID number'
}

export const OCCUPATION_TYPES: Record<string, IVehicleUsages> = {
  MINING_AND_ENGINEERING: {
    title: 'Mining and Engineering',
    description: ''
  },
  UNEMPLOYED: {
    title: 'Unemployed',
    description: ''
  },
  COMMERCIAL_DRIVER: {
    title: 'Commercial Driver',
    description: ''
  },
  SELF_EMPLOYED: {
    title: 'Self Employed',
    description: ''
  },
  SALESPERSON: {
    title: 'Sales Person',
    description: ''
  },
  HEALTH_WORKER: {
    title: 'Health Worker',
    description: ''
  },
  SECURITY_SERVICES: {
    title: 'Security Services',
    description: ''
  },
  TRAVEL_AGENT: {
    title: 'Travel Agent',
    description: ''
  },
  ANY_OTHER_PROFESSION: {
    title: 'Any Other Profession',
    description: ''
  },
  BUSINESS_EXECUTIVE: {
    title: 'Business Executive',
    description: ''
  }
};

export const libraries: Array<'drawing' | 'places' | 'geometry'> = [
  'drawing',
  'places',
  'geometry'
];

export const MINIMUM_ALLOWABLE_MANUFACTURING_YEAR = new Date().getFullYear() - 23;

export const EMPLOYEE_INCOME_BAND = [
  '1,000.00 - 5,000.00',
  '5,0001.00 - 10,000.00',
  '10,0001.00 - 20,000.00',
  '20,001.00 +'
];

export const EMPLOYEE_INCOME_BAND_NAIRA = [
  '800,000.00 - 4,000,000.00',
  '4,000,000.00 - 8,000,000.00',
  '8,000,000.00 - 16,000,000.00',
  '16,000,000.00 +'
];

export const RIDE_SHARING_DAILY_AVERAGE_INCOME = [
  'Below 200.00',
  '200.00 - 399.00',
  '400.00 - 599.00',
  '600.00 - 799.00',
  '800.00 - 999.00',
  '1000.00 +'
];

export const SUCCESS_PAYMENT_STATUS_COLOR = `border border-green-700 text-green-700`;
export const NEW = `border border-[#00ADC6] text-[#00ADC6]`;
export const EXISTING_POLICY_STATUS_COLOR = `border border-slate-500 text-slate-500`;
export const DRAFT = `border border-[#00c68e] text-[#00c68e]`;
export const IN_APPROVAL_PAYMENT_STATUS_COLOR = `border border-orange-500 text-orange-500 `;
export const IN_REVIEW_PAYMENT_STATUS_COLOR = `border border-green-400 text-green-400`;
export const ON_HOLD_PAYMENT_STATUS_COLOR = `border border-[#ffd700] text-[#ffd700]`;
export const DECLINED_PAYMENT_STATUS_COLOR = `border border-[#ff9999] text-[#ff9999]`;
export const CANCELLED_PAYMENT_STATUS_COLOR = `border border-[#808080] text-[#808080]`;
export const ARCHIVED_POLICY_STATUS_COLOR = `border border-[#E8833A] text-[#E8833A]`;
export const DUE_LATER_POLICY_STATUS_COLOR = `border border-gray-500] text-gray-500`;
export const EXPIRED_POLICY_STATUS_COLOR = `border border-[#ffa07a] text-[#ffa07a]`;
export const OVERDUE_STATUS_COLOR = `border border-[#ff6f69] text-[#ff6f69]`;

export const COMPREHENSIVE_100_COLOR = `border border-green-800 text-green-800`;
export const COMPREHENSIVE_90_COLOR = `border border-teal-500 text-teal-500`;
export const COMPREHENSIVE_85_COLOR = `border border-brown-500 text-brown-500`;
export const SMART_DRIVE_COLOR = `border border-orange-600 text-orange-600`;
export const THIRD_PARTY_COLOR = `border border-yellow-500 text-yellow-500`;
export const THIRD_PARTY_THEFT_COLOR = `border border-orange-500 text-orange-500`;
export const COMMISSIONS_WITHHOLDING_TAX_PERCENTAGE = 5;

export const DISCOUNT_ACTIVE_STATUS_COLOR = 'border border-[#006400] text-[#006400]';
export const DISCOUNT_INACTIVE_STATUS_COLOR = 'border border-[#FF5E5B] text-[#FF5E5B]';

export const APPROVAL_TYPE_POLICY_COLOR = `border border-yellow-400 text-yellow-400`;
export const APPROVAL_TYPE_PAYMENT_COLOR = `border border-green-500 text-green-500`;
export const APPROVAL_TYPE_LEAD_COLOR = `border border-orange-500 text-orange-500`;
export const APPROVAL_TYPE_CUSTOMER_COLOR = `border border-blue-500 text-blue-500`;


export const SUPER_ADMINISTRATOR_COLOR = `border border-[#8B0000] text-[#8B0000]`; // dark red
export const ADMINISTRATOR_COLOR = `border border-[#1E90FF] text-[#1E90FF]`; // dodger blue

export const INSURER_ADMIN_COLOR = `border border-[#2E8B57] text-[#2E8B57]`; // sea green
export const INSURER_AGENT_COLOR = `border border-[#4682B4] text-[#4682B4]`; // steel blue
export const INSURER_TECH_ADMIN_COLOR = `border border-[#6A5ACD] text-[#6A5ACD]`; // slate blue
export const INSURER_POLICY_ADMIN_COLOR = `border border-[#20B2AA] text-[#20B2AA]`; // light sea green
export const INSURER_FINANCE_ADMIN_COLOR = `border border-[#DAA520] text-[#DAA520]`; // goldenrod
export const INSURER_CLAIM_ADMIN_COLOR = `border border-[#CD5C5C] text-[#CD5C5C]`; // indian red

export const INSURER_MARKETING_ADMIN_COLOR = `border border-[#FF8C00] text-[#FF8C00]`; // dark orange
export const INSURER_UNDERWRITER_COLOR = `border border-[#9932CC] text-[#9932CC]`; // dark orchid

export const AGENCY_ADMIN_COLOR = `border border-[#3CB371] text-[#3CB371]`; // medium sea green
export const AGENT_COLOR = `border border-[#6495ED] text-[#6495ED]`; // cornflower blue

export const REVIEWER_COLOR = `border border-[#708090] text-[#708090]`; // slate gray
export const RIDE_SHARING_ADMIN_COLOR = `border border-[#B22222] text-[#B22222]`; // firebrick
export const RIDE_SHARING_AGENT_COLOR = `border border-[#FF4500] text-[#FF4500]`; // orange red

export const FLEET_ADMIN_COLOR = `border border-[#556B2F] text-[#556B2F]`; // dark olive green
export const FLEET_MANAGER_COLOR = `border border-[#808000] text-[#808000]`; // olive

export const FUEL_ADMIN_COLOR = `border border-[#A0522D] text-[#A0522D]`; // sienna
export const FUEL_MANAGER_COLOR = `border border-[#D2691E] text-[#D2691E]`; // chocolate
export const FUEL_VENDOR_COLOR = `border border-[#BC8F8F] text-[#BC8F8F]`; // rosy brown

export const TECHNICAL_COLOR = `border border-[#2F4F4F] text-[#2F4F4F]`; // dark slate gray
export const BROKER_COLOR = `border border-[#468499] text-[#468499]`; // custom muted blue
export const TEAM_MANAGER_COLOR = `border border-[#4169E1] text-[#4169E1]`; // royal blue
export const MANAGER_COLOR = `border border-[#FF0000] text-[#FF0000]`;
export const WORKSPACE_OWNER_COLOR = `border border-[#FF69B4] text-[#FF69B4]`;
export const SUPERVISOR_COLOR = `border border-[#059669] text-[#059669]`;


export const BANKS_OF_GHANA = [
  'Absa Bank Ghana Limited',
  'Access Bank Ghana Plc',
  'Agricultural Development Bank of Ghana',
  'Bank of Africa Ghana Limited',
  'CalBank Limited',
  'Consolidated Bank Ghana Limited',
  'Ecobank Ghana Limited',
  'FBN Bank Ghana Limited',
  'Fidelity Bank Ghana Limited',
  'First Atlantic Bank Limited',
  'First National Bank Ghana',
  'GCB Bank Limited',
  'Guaranty Trust Bank Ghana Limited',
  'National Investment Bank Limited',
  'OmniBSIC Bank Ghana Limited',
  'Prudential Bank Limited',
  'Republic Bank Ghana Limited',
  'Société Générale Ghana Limited',
  'Stanbic Bank Ghana Limited',
  'Standard Chartered Bank Ghana Limited',
  'United Bank for Africa Ghana Limited',
  'Universal Merchant Bank Limited',
  'Zenith Bank Ghana Limited.'
];

export const GHANA_TELCOS = [
  'MTN',
  'Telecel',
  'AirtelTigo'
]

export const COUNTRY_OPTIONS = ['Ghana'];

export const LeadsDonutBgColors = [
  '#FCD36C',
  '#6EE0AB',
  '#00ADC6',
  '#666578',
  '#FBC02D',
  '#000000',
  '#807E7B',
  '#7E6017',
  '#027180',
  '#22935F'
];

export const ChannelDonutsBgColors = [
  '#6EE0AB',
  '#FCD36C',
  '#00ADC6',
  '#666578',
  '#027180',
  '#FD1D1D'
];

export const SalesMixDonutsBgColors = ['#FBC02D', '#30D287', '#6366F1', '#9AC6CC', '#00ADC6'];

export const LeadCategoriesChartBgColors = [
  '#FDE6AB',
  '#ACEDCF',
  '#99DEE8',
  '#D3D3DE',
  '#999999',
  '#9AC6CC',
  '#FFFCF5',
  '#9190AC'
];

export const InsurerAgentRoles = [
  'Chief Executive Officer',
  'General Manager',
  'Operations Manager',
  'Accountant',
  'Software Developer',
  'Sales',
  'Marketing'
];

export const MONTHS_OF_THE_YEAR = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export const ALPHABETS = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z'
];


export const TAG_NEW_COLOR = `border border-[#57A9F4] text-[#57A9F4]`;
export const TAG_CONTACTED_COLOR = `border border-[#F18F34] text-[#F18F34]`;
export const TAG_QUALIFIED_COLOR = `border border-[#c1e5c1] text-[#c1e5c1]`;
export const TAG_UNRESPONSIVE_COLOR = `border border-[#E8AA0E] text-[#E8AA0E]`;
export const TAG_DISQUALIFIED_COLOR = `border border-[#F06565] text-[#F06565]`;
export const TAG_IN_NEGOTIATION_COLOR = `border border-[#4F5357] text-[#4F5357]`;
export const TAG_NURTURING_COLOR = `border border-gray-500 text-gray-500`;
export const TAG_CONVERTED_COLOR = `border border-[#006400] text-[#006400]`;
export const TAG_NA_COLOR = `border border-[#BFC9CA]`;


export const NEW_OWNER_YET_TO_CHANGE_AT_DVLA = 'New owner, yet to change at DVLA';
export const NEW_OWNER_YET_TO_CHANGE_AT_FRSC = 'New owner, yet to change at FRSC';
export const WORK_AND_PAY_DRIVER = 'Work & Pay Driver';
export const EMPLOYEE_DRIVER = 'Employee Driver';
export const OTHER_AUTHORISED_KEEPER_OR_USER = 'Other authorised keeper/user';
export const OWNER_IN_DVLA_RECORDS = 'Owner in DVLA records';

export const getPriorityColor = (category: string) => {
  switch (category) {
    case 'HIGH':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'LOW':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'URGENT':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-blue-100 text-blue-800 border-blue-200';
  }
};

export const getCategoryColor = (category: string) => {
  switch (category.toUpperCase()) {

    case "CLAIM":
      return "bg-purple-100 text-purple-800 border-purple-200";

    case "COMMISSION":
      return "bg-amber-100 text-amber-800 border-amber-200";

    case "COMMUNITY":
      return "bg-teal-100 text-teal-800 border-teal-200";

    case "CUSTOMER":
      return "bg-green-100 text-green-800 border-green-200";

    case "INSURER":
      return "bg-indigo-100 text-indigo-800 border-indigo-200";

    case "LEAD":
      return "bg-orange-100 text-orange-800 border-orange-200";

    case "LOAN":
      return "bg-yellow-100 text-yellow-800 border-yellow-200";

    case "PAYMENT":
      return "bg-blue-100 text-blue-800 border-blue-200";

    case "PAYOUT":
      return "bg-cyan-100 text-cyan-800 border-cyan-200";

    case "POLICY":
      return "bg-red-100 text-red-800 border-red-200";

    case "PREMIUM_FINANCING":
      return "bg-lime-100 text-lime-800 border-lime-200";

    case "QUOTE":
      return "bg-pink-100 text-pink-800 border-pink-200";

    case "STAFF":
      return "bg-slate-100 text-slate-800 border-slate-200";

    case "TASK":
      return "bg-violet-100 text-violet-800 border-violet-200";

    case "TEAM":
      return "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200";

    case "VEHICLE":
      return "bg-stone-100 text-stone-800 border-stone-200";

    case "VEHICLE_INSPECTION":
      return "bg-emerald-100 text-emerald-800 border-emerald-200";

    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

export const POLICY_ISSUANCE_MODES_WITH_DESCRIPTION: Record<string, any> = {
  INSURER_API: {
    title: 'Insurer API',
    description: "Policy issued directly through the insurer's API integration"
  },
  MID_LIVE: {
    title: 'MID Live',
    description: 'Policy issued through the MID (Motor Insurance Database) live system'
  },
  EMAIL: {
    title: 'Email',
    description: 'Policy issued and delivered via email communication'
  },
  OFFLINE: {
    title: 'Manual',
    description: 'Policy issued through manual/offline processing'
  },
};

export const VEHICLE_BODY_TYPE_MAP: Record<string, VEHICLE_BODY_TYPES> = {
  pickup: VEHICLE_BODY_TYPES.PICKUP,
  'saloon sedan': VEHICLE_BODY_TYPES.SALOON_SEDAN,
  saloon: VEHICLE_BODY_TYPES.SALOON_SEDAN,
  'mini small bus': VEHICLE_BODY_TYPES.MINI_SMALL_BUS,
  'maxi big bus': VEHICLE_BODY_TYPES.MAXI_BIG_BUS,
  'articulated truck': VEHICLE_BODY_TYPES.ARTICULATED_TRUCK,
  motorcycle: VEHICLE_BODY_TYPES.MOTOR_BIKE,
  sedan: VEHICLE_BODY_TYPES.SEDAN,
  hatchback: VEHICLE_BODY_TYPES.HATCHBACK,
  coupe: VEHICLE_BODY_TYPES.COUPE,
  convertible: VEHICLE_BODY_TYPES.CONVERTIBLE,
  wagon: VEHICLE_BODY_TYPES.WAGON,
  crossover: VEHICLE_BODY_TYPES.CROSSOVER,
  suv: VEHICLE_BODY_TYPES.SUV,
  minivan: VEHICLE_BODY_TYPES.MINIVAN,
  'luxury sedan': VEHICLE_BODY_TYPES.LUXURY_SEDAN,
  'sports car': VEHICLE_BODY_TYPES.SPORTS_CAR,
  supercar: VEHICLE_BODY_TYPES.SUPERCAR,
  hypercar: VEHICLE_BODY_TYPES.HYPERCAR,
  liftback: VEHICLE_BODY_TYPES.LIFTBACK,
  notchback: VEHICLE_BODY_TYPES.NOTCHBACK,
  fastback: VEHICLE_BODY_TYPES.FASTBACK,
  'targa top': VEHICLE_BODY_TYPES.TARGA_TOP,
  phaeton: VEHICLE_BODY_TYPES.PHAETON,
  'pickup truck light': VEHICLE_BODY_TYPES.PICKUP_TRUCK_LIGHT,
  'pickup truck heavy': VEHICLE_BODY_TYPES.PICKUP_TRUCK_HEAVY,
  'box truck': VEHICLE_BODY_TYPES.BOX_TRUCK,
  'flatbed truck': VEHICLE_BODY_TYPES.FLATBED_TRUCK,
  'dump truck': VEHICLE_BODY_TYPES.DUMP_TRUCK,
  'semi truck': VEHICLE_BODY_TYPES.SEMI_TRUCK,
  'panel van': VEHICLE_BODY_TYPES.PANEL_VAN,
  'cargo van': VEHICLE_BODY_TYPES.CARGO_VAN,
  'step van': VEHICLE_BODY_TYPES.STEP_VAN,
  'delivery van small': VEHICLE_BODY_TYPES.DELIVERY_VAN_SMALL,
  'service truck': VEHICLE_BODY_TYPES.SERVICE_TRUCK,
  'tow truck': VEHICLE_BODY_TYPES.TOW_TRUCK,
  'fire truck': VEHICLE_BODY_TYPES.FIRE_TRUCK,
  ambulance: VEHICLE_BODY_TYPES.AMBULANCE,
  'garbage truck': VEHICLE_BODY_TYPES.GARBAGE_TRUCK,
  'tanker truck': VEHICLE_BODY_TYPES.TANKER_TRUCK,
  'concrete mixer truck': VEHICLE_BODY_TYPES.CONCRETE_MIXER_TRUCK,
  'logging truck': VEHICLE_BODY_TYPES.LOGGING_TRUCK,
  'bus passenger': VEHICLE_BODY_TYPES.BUS_PASSENGER,
  'school bus': VEHICLE_BODY_TYPES.SCHOOL_BUS,
  motorcoach: VEHICLE_BODY_TYPES.MOTORCOACH,
  'articulated bus': VEHICLE_BODY_TYPES.ARTICULATED_BUS,
  minibus: VEHICLE_BODY_TYPES.MINIBUS,
  'passenger van': VEHICLE_BODY_TYPES.PASSENGER_VAN,
  'conversion van': VEHICLE_BODY_TYPES.CONVERSION_VAN,
  'camper van': VEHICLE_BODY_TYPES.CAMPER_VAN,
  'rv class a': VEHICLE_BODY_TYPES.RV_CLASS_A,
  'rv class c': VEHICLE_BODY_TYPES.RV_CLASS_C,
  motorhome: VEHICLE_BODY_TYPES.MOTORHOME,
  limousine: VEHICLE_BODY_TYPES.LIMOUSINE,
  'amphibious vehicle': VEHICLE_BODY_TYPES.AMPHIBIOUS_VEHICLE,
  'dune buggy': VEHICLE_BODY_TYPES.DUNE_BUGGY,
  'go kart': VEHICLE_BODY_TYPES.GO_KART,
  atv: VEHICLE_BODY_TYPES.ATV,
  utv: VEHICLE_BODY_TYPES.UTV,
  snowmobile: VEHICLE_BODY_TYPES.SNOWMOBILE,
  'golf cart': VEHICLE_BODY_TYPES.GOLF_CART,
  'tracked vehicle': VEHICLE_BODY_TYPES.TRACKED_VEHICLE,
  hearse: VEHICLE_BODY_TYPES.HEARSE,
  'motorcycle standard': VEHICLE_BODY_TYPES.MOTORCYCLE_STANDARD,
  'motorcycle cruiser': VEHICLE_BODY_TYPES.MOTORCYCLE_CRUISER,
  'motorcycle sport': VEHICLE_BODY_TYPES.MOTORCYCLE_SPORT,
  'motorcycle touring': VEHICLE_BODY_TYPES.MOTORCYCLE_TOURING,
  'motorcycle dual sport': VEHICLE_BODY_TYPES.MOTORCYCLE_DUAL_SPORT,
  'motorcycle dirt': VEHICLE_BODY_TYPES.MOTORCYCLE_DIRT,
  scooter: VEHICLE_BODY_TYPES.SCOOTER,
  moped: VEHICLE_BODY_TYPES.MOPED,
  'motorcycle chopper': VEHICLE_BODY_TYPES.MOTORCYCLE_CHOPPER,
  trike: VEHICLE_BODY_TYPES.TRIKE,
  'sidecar motorcycle': VEHICLE_BODY_TYPES.SIDECAR_MOTORCYCLE,
  'electric car': VEHICLE_BODY_TYPES.ELECTRIC_CAR,
  'hybrid car': VEHICLE_BODY_TYPES.HYBRID_CAR,
  'classic car': VEHICLE_BODY_TYPES.CLASSIC_CAR,
  'custom build': VEHICLE_BODY_TYPES.CUSTOM_BUILD,
  'concept car': VEHICLE_BODY_TYPES.CONCEPT_CAR,
  'race car': VEHICLE_BODY_TYPES.RACE_CAR,
  nev: VEHICLE_BODY_TYPES.NEV,
  autocar: VEHICLE_BODY_TYPES.AUTOCYCLE,
  crane: VEHICLE_BODY_TYPES.CRANE,
  excavator: VEHICLE_BODY_TYPES.EXCAVATOR,
  forklift: VEHICLE_BODY_TYPES.FORKLIFT,
  hammerhead: VEHICLE_BODY_TYPES.HAMMERHEAD,
  hilux: VEHICLE_BODY_TYPES.HILUX,
  bulldozer: VEHICLE_BODY_TYPES.BULLDOZER,
  'station wagon': VEHICLE_BODY_TYPES.STATION_WAGON
};
